import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  IconButton,
  Card,
  CardContent,
  Grid,
  Button,
  Toolbar,
  AppBar,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  Modal
} from '@mui/material';
import {
  Folder as FolderIcon,
  Description as FileIcon,
  ArrowBack as ArrowBackIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as DocIcon,
  Image as ImageIcon,
  TableChart as SpreadsheetIcon,
  ViewList as ListViewIcon,
  ViewModule as GridViewIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  MoreHoriz as MoreIcon
} from '@mui/icons-material';
import api from '../api/axios';
// Import the PDF Viewer component
import PDFViewer from './PDFViewer';

interface DocumentItem {
  id: string;
  name: string;
  webUrl?: string;
  folder?: { childCount: number };
  file?: { mimeType: string };
  size?: number;
  createdDateTime?: string;
  lastModifiedDateTime?: string;
}

interface DriveItem {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

interface BreadcrumbItem {
  id: string;
  name: string;
  driveId: string;
  type: 'drive' | 'folder';
}

const DocumentBrowser: React.FC = () => {
  const [drives, setDrives] = useState<DriveItem[]>([]);
  const [items, setItems] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentDriveId, setCurrentDriveId] = useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  
  // Document viewing states
  const [selectedFile, setSelectedFile] = useState<DocumentItem | null>(null);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [fileViewUrl, setFileViewUrl] = useState<string>('');
  
  // UI enhancement states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<'name' | 'modified' | 'type'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [officeViewerOpen, setOfficeViewerOpen] = useState(false);

  // Format file size in human-readable format
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Select icon based on file type
  const getFileIcon = (item: DocumentItem) => {
    const iconStyle = { fontSize: viewMode === 'grid' ? 64 : 28 };
    
    if (item.folder) {
      return <FolderIcon color="primary" sx={{ ...iconStyle, color: '#0078d4' }} />;
    }
    
    if (item.file) {
      const mimeType = item.file.mimeType;
      const fileName = item.name.toLowerCase();
      const extension = fileName.split('.').pop();
      
      // PDF files
      if (mimeType.includes('pdf') || extension === 'pdf') {
        return <PdfIcon sx={{ ...iconStyle, color: '#e74c3c' }} />;
      }
      
      // Word documents
      if (mimeType.includes('word') || 
          mimeType.includes('document') || 
          extension === 'doc' || 
          extension === 'docx') {
        return <DocIcon sx={{ ...iconStyle, color: '#2b579a' }} />;
      }
      
      // Excel spreadsheets
      if (mimeType.includes('excel') || 
          mimeType.includes('sheet') || 
          extension === 'xls' || 
          extension === 'xlsx' ||
          extension === 'csv') {
        return <SpreadsheetIcon sx={{ ...iconStyle, color: '#217346' }} />;
      }
      
      // Images
      if (mimeType.includes('image') || 
          ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(extension || '')) {
        return <ImageIcon sx={{ ...iconStyle, color: '#0099e5' }} />;
      }
    }
    
    // Default file icon
    return <FileIcon sx={{ ...iconStyle, color: '#777777' }} />;
  };

  // Load document libraries/drives
  const loadDrives = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/documents');
      setDrives(response.data.value || []);
      setItems([]);
      setCurrentDriveId(null);
      setCurrentFolderId(null);
      setBreadcrumbs([]);
    } catch (err: any) {
      console.error('Error loading document libraries:', err);
      setError(err.response?.data?.error || 'Failed to load document libraries');
    } finally {
      setLoading(false);
    }
  };

  // Load folders and files within a drive or folder
  const loadItems = async (driveId: string, itemId?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Loading items from drive ${driveId}, folder ${itemId || 'root'}`);
      const response = await api.get(`/api/documents/${driveId}/items/${itemId || ''}`);
      console.log('Loaded items:', response.data);
      setItems(response.data.value || []);
      
      // Update breadcrumbs
      if (!itemId) {
        // Root of a drive
        const drive = drives.find(d => d.id === driveId);
        if (drive) {
          setBreadcrumbs([{
            id: 'root',
            name: drive.name,
            driveId,
            type: 'drive'
          }]);
        }
      }
      
      setCurrentDriveId(driveId);
      setCurrentFolderId(itemId || 'root');
    } catch (err: any) {
      console.error('Error loading document items:', err);
      setError(err.response?.data?.error || 'Failed to load document items');
    } finally {
      setLoading(false);
    }
  };

  // Handle drive selection
  const handleDriveClick = (drive: DriveItem) => {
    loadItems(drive.id);
    setBreadcrumbs([{
      id: 'root',
      name: drive.name,
      driveId: drive.id,
      type: 'drive'
    }]);
  };

  // Is this an Office document
  const isOfficeDocument = (mimeType: string): boolean => {
    return mimeType.includes('word') || 
      mimeType.includes('document') ||
      mimeType.includes('excel') || 
      mimeType.includes('sheet') ||
      mimeType.includes('powerpoint') || 
      mimeType.includes('presentation');
  };
  
  // Handle folder click
  const handleFolderClick = (item: DocumentItem) => {
    if (currentDriveId) {
      console.log('Navigating to folder:', item.id, item.name);
      loadItems(currentDriveId, item.id);
      
      // Update breadcrumbs
      setBreadcrumbs(prev => [
        ...prev,
        {
          id: item.id,
          name: item.name,
          driveId: currentDriveId,
          type: 'folder'
        }
      ]);
    }
  };
  
  // Handle file click (view file)
  const handleFileClick = (item: DocumentItem) => {
    if (currentDriveId) {
      // Save selected file
      setSelectedFile(item);
      
      // Get correct file URL
      const url = `/api/documents/${currentDriveId}/content/${item.id}`;
      setFileViewUrl(url);
      
      // Handle file based on type
      if (item.file?.mimeType.includes('pdf')) {
        // Open PDF viewer
        setPdfViewerOpen(true);
      } else if (isOfficeDocument(item.file?.mimeType || '')) {
        // For Office documents, show in embedded Office Online Viewer
        const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(window.location.origin + url)}`;
        setFileViewUrl(officeUrl);
        setOfficeViewerOpen(true);
      } else {
        // For other files, open directly
        window.open(url, '_blank');
      }
    }
  };
  
  // Handle file download
  const handleFileDownload = (item: DocumentItem) => {
    if (currentDriveId) {
      const url = `/api/documents/${currentDriveId}/content/${item.id}`;
      
      // Create an anchor element and simulate a click to download
      const a = document.createElement('a');
      a.href = url;
      a.download = item.name; // Set the filename
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };
  
  // Main item click handler
  const handleItemClick = (item: DocumentItem) => {
    if (item.folder) {
      handleFolderClick(item);
    } else if (item.file) {
      handleFileClick(item);
    }
  };

  // Navigate to specific breadcrumb
  const handleBreadcrumbClick = (breadcrumb: BreadcrumbItem, index: number) => {
    if (index === 0) {
      // Clicked on drive name (root level)
      loadItems(breadcrumb.driveId);
    } else {
      // Clicked on folder
      loadItems(breadcrumb.driveId, breadcrumb.id);
    }
    
    // Update breadcrumbs array
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  };

  // Go back to document libraries view
  const handleBackToLibraries = () => {
    loadDrives();
  };

  // Initial load of document libraries
  // Check if we're in setup mode
  const isSetupMode = window.location.search.includes('setup=true');

  // Handle sorting
  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortChange = (sortType: 'name' | 'modified' | 'type') => {
    if (sortBy === sortType) {
      // Toggle direction if clicking the same sort option
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort type with default ascending direction
      setSortBy(sortType);
      setSortDirection('asc');
    }
    handleSortClose();
  };

  // Handle search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Handle closing PDF viewer
  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
  };

  // Refresh current folder
  const handleRefresh = () => {
    if (currentDriveId) {
      loadItems(currentDriveId, currentFolderId);
    }
  };

  // Filter and sort items
  const filteredItems = React.useMemo(() => {
    let result = [...items];
    
    // Apply search filter
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.name.toLowerCase().includes(term)
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      // Always sort folders first
      if (a.folder && !b.folder) return -1;
      if (!a.folder && b.folder) return 1;
      
      // Then apply selected sort
      switch (sortBy) {
        case 'name':
          return sortDirection === 'asc' 
            ? a.name.localeCompare(b.name) 
            : b.name.localeCompare(a.name);
        case 'modified':
          if (!a.lastModifiedDateTime || !b.lastModifiedDateTime) return 0;
          return sortDirection === 'asc'
            ? new Date(a.lastModifiedDateTime).getTime() - new Date(b.lastModifiedDateTime).getTime()
            : new Date(b.lastModifiedDateTime).getTime() - new Date(a.lastModifiedDateTime).getTime();
        case 'type':
          // Get file extensions
          const extA = a.name.split('.').pop() || '';
          const extB = b.name.split('.').pop() || '';
          return sortDirection === 'asc'
            ? extA.localeCompare(extB)
            : extB.localeCompare(extA);
        default:
          return 0;
      }
    });
    
    return result;
  }, [items, searchTerm, sortBy, sortDirection]);

  // Initial load - check for setup mode
  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // In setup mode, load all drives to find IDs
    // Otherwise, go straight to the restricted folder
    const endpoint = isSetupMode ? '/api/documents?setup=true' : '/api/documents';
    
    api.get(endpoint)
      .then(response => {
        console.log('Document API response:', response.data);
        
        if (isSetupMode && response.data.value) {
          // In setup mode, show drives with IDs
          console.log('Setup mode - showing drives:', response.data.value);
          setDrives(response.data.value || []);
          setCurrentDriveId(null); // Reset to drives view
          setBreadcrumbs([]); // Clear breadcrumbs in setup mode
        } else {
          // Normal mode - show folder contents
          console.log('Normal mode - showing items:', response.data.value || []);
          setItems(response.data.value || []);
          
          // Set default breadcrumb for normal mode
          if (response.data.value?.length > 0) {
            // When using the normal mode, we're fetching data from the server's configured drive
            // This ID should match what's configured on the server
            // The server automatically restricts to the MS_SHAREPOINT_DRIVE_ID in .env
            const driveId = "b!5JMNrdIqlE-S_vCsG1pDToXe9DhJ19pBnsdZo_PPh0fOM2jgt7NUQrRf9qNEI4B9";
            console.log('Setting current drive ID to:', driveId);
            setCurrentDriveId(driveId);
            
            // Set breadcrumb for the root folder
            setBreadcrumbs([{
              id: 'root',
              name: 'Shared Documents',
              driveId: driveId,
              type: 'drive'
            }]);
          }
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading documents:', err);
        setError(err.response?.data?.error || 'Failed to load documents');
        setLoading(false);
      });
  }, [isSetupMode]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* SharePoint-like top AppBar */}
      <AppBar position="static" color="default" elevation={0} sx={{ 
        backgroundColor: 'white', 
        borderBottom: '1px solid #e0e0e0' 
      }}>
        <Toolbar sx={{ flexWrap: 'wrap', p: { xs: 1, sm: 2 } }}>
          <Typography variant="h6" component="div" sx={{ 
            flexGrow: 1, 
            color: '#0078d4',
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}>
            {isSetupMode ? 'Document Browser - Setup Mode' : 'Document Browser'}
          </Typography>
          
          {!isSetupMode && (
            <>
              <Box sx={{ 
                display: 'flex', 
                width: { xs: '100%', sm: 'auto' }, 
                mt: { xs: 1, sm: 0 },
                order: { xs: 2, sm: 1 }
              }}>
                <TextField
                  placeholder="Search"
                  size="small"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    mr: 1, 
                    backgroundColor: '#f5f5f5', 
                    width: { xs: '100%', sm: '200px' },
                    flexGrow: { xs: 1, sm: 0 }
                  }}
                />
                
                <IconButton onClick={handleRefresh} size="small">
                  <RefreshIcon />
                </IconButton>
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                ml: { xs: 'auto', sm: 1 },
                order: { xs: 1, sm: 2 }
              }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, newMode) => { if(newMode) setViewMode(newMode) }}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  <ToggleButton value="list" aria-label="list view">
                    <ListViewIcon />
                  </ToggleButton>
                  <ToggleButton value="grid" aria-label="grid view">
                    <GridViewIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
                
                <IconButton onClick={handleSortClick} size="small">
                  <SortIcon />
                </IconButton>
                <Menu
                  anchorEl={sortAnchorEl}
                  open={Boolean(sortAnchorEl)}
                  onClose={handleSortClose}
                >
                  <MenuItem 
                    onClick={() => handleSortChange('name')}
                    selected={sortBy === 'name'}
                  >
                    Name {sortBy === 'name' && (sortDirection === 'asc' ? '▲' : '▼')}
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleSortChange('modified')}
                    selected={sortBy === 'modified'}
                  >
                    Modified {sortBy === 'modified' && (sortDirection === 'asc' ? '▲' : '▼')}
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleSortChange('type')}
                    selected={sortBy === 'type'}
                  >
                    Type {sortBy === 'type' && (sortDirection === 'asc' ? '▲' : '▼')}
                  </MenuItem>
                </Menu>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>
      
      {/* Breadcrumb navigation */}
      <Paper sx={{ p: { xs: 1, sm: 1.5 }, mb: 1, borderRadius: 0, backgroundColor: '#f5f5f5' }}>
        {isSetupMode ? (
          <>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Setup Mode - Select a Drive and Folder
            </Typography>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Note the ID of the drive and folder you want to restrict access to,
              then add them to your .env file as MS_SHAREPOINT_DRIVE_ID and MS_SHAREPOINT_FOLDER_ID
            </Typography>
          </>
        ) : (
          <>
            {/* Back button for mobile only - shown when in subfolder */}
            {breadcrumbs.length > 1 && (
              <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 1 }}>
                <Button
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  variant="outlined"
                  onClick={() => {
                    const parentIndex = breadcrumbs.length > 1 ? breadcrumbs.length - 2 : 0;
                    handleBreadcrumbClick(breadcrumbs[parentIndex], parentIndex);
                  }}
                  sx={{ py: 0.5, textTransform: 'none' }}
                >
                  Back to {breadcrumbs[breadcrumbs.length > 1 ? breadcrumbs.length - 2 : 0].name}
                </Button>
              </Box>
            )}
            
            {/* Standard breadcrumbs - reduced on mobile */}
            {breadcrumbs.length > 0 && (
              <Breadcrumbs 
                sx={{ 
                  py: 0.5,
                  '.MuiBreadcrumbs-ol': {
                    flexWrap: { xs: 'nowrap', sm: 'wrap' }
                  },
                  '.MuiBreadcrumbs-li': {
                    whiteSpace: 'nowrap'
                  }
                }}
                maxItems={3}
              >
                {breadcrumbs.map((crumb, index) => (
                  <Link
                    key={crumb.id}
                    component="button"
                    variant="body2"
                    color={index === breadcrumbs.length - 1 ? 'primary' : 'inherit'}
                    onClick={() => handleBreadcrumbClick(crumb, index)}
                    sx={{ 
                      cursor: 'pointer',
                      fontWeight: index === breadcrumbs.length - 1 ? 'bold' : 'normal',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    {index === 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FolderIcon fontSize="small" sx={{ mr: 0.5 }} />
                        {/* On mobile, show only icon for root */}
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{crumb.name}</Box>
                      </Box>
                    ) : (
                      // For intermediate folders, show truncated names on mobile
                      <Typography
                        component="span"
                        noWrap
                        sx={{ 
                          maxWidth: { xs: '80px', sm: '150px', md: 'none' },
                          display: 'inline-block',
                          verticalAlign: 'middle'
                        }}
                      >
                        {crumb.name}
                      </Typography>
                    )}
                  </Link>
                ))}
              </Breadcrumbs>
            )}
            
            {/* Current folder name for mobile - shown more prominently */}
            {breadcrumbs.length > 0 && (
              <Typography 
                variant="subtitle2" 
                component="div" 
                sx={{ 
                  display: { xs: 'block', sm: 'none' },
                  color: 'primary.main',
                  fontWeight: 'bold',
                  mt: 1,
                  textAlign: 'center'
                }}
              >
                {breadcrumbs[breadcrumbs.length - 1].name}
              </Typography>
            )}
          </>
        )}
      </Paper>
      
      {/* Main content */}
      <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
        {error && (
          <Alert severity={error.includes("temporarily disabled") ? "info" : "error"} sx={{ mb: 2 }}>
            {error}
            {error.includes("temporarily disabled") && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Document access is currently disabled during our testing phase. 
                  Full document access will be available once your account is fully activated.
                </Typography>
              </Box>
            )}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : isSetupMode && !currentDriveId ? (
          // Setup Mode - Show drives with IDs
          <Box>
            <Typography variant="h6" color="primary" gutterBottom>
              Available Document Libraries (Drives)
            </Typography>
            
            {!drives || drives.length === 0 ? (
              <Alert severity="info">
                No document libraries found. Please check your Microsoft 365 connection.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {drives.map((drive) => (
                  <Grid item xs={12} sm={6} md={4} key={drive.id}>
                    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <FolderIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1">{drive.name}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Drive ID:</strong> {drive.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Type:</strong> {drive.driveType}
                      </Typography>
                      <Box sx={{ mt: 'auto', pt: 1 }}>
                        <Button 
                          variant="contained" 
                          size="small" 
                          fullWidth
                          onClick={() => handleDriveClick(drive)}
                        >
                          Browse to Find Folder ID
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        ) : isSetupMode && currentDriveId ? (
          // Setup Mode - Show folders with IDs
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Button 
                variant="outlined" 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBackToLibraries}
                sx={{ mr: 2 }}
              >
                Back to Drives
              </Button>
              <Typography variant="h6" color="primary">
                Browsing Drive: {currentDriveId}
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              {items.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {getFileIcon(item)}
                      <Typography variant="subtitle1" sx={{ ml: 1 }}>{item.name}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Item ID:</strong> {item.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {item.folder 
                        ? `Contains ${item.folder.childCount} items` 
                        : `File type: ${item.file?.mimeType || 'Unknown'}, Size: ${formatFileSize(item.size)}`
                      }
                    </Typography>
                    {item.folder && (
                      <Box sx={{ mt: 'auto', pt: 1 }}>
                        <Button 
                          variant="contained" 
                          size="small" 
                          fullWidth
                          onClick={() => handleItemClick(item)}
                        >
                          Browse Inside
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
            
            {items.length === 0 && (
              <Alert severity="info">This folder is empty</Alert>
            )}
          </Box>
        ) : viewMode === 'list' ? (
          // Normal Mode - List View
          <Paper elevation={0} sx={{ mb: 2, overflow: 'hidden', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, // Hide on mobile
              backgroundColor: '#f5f5f5', 
              p: 1.5,
              borderBottom: '1px solid #e0e0e0',
              fontWeight: 'bold'
            }}>
              <Box sx={{ width: '40%' }}>Name</Box>
              <Box sx={{ width: '20%' }}>Modified</Box>
              <Box sx={{ width: '15%' }}>Size</Box>
              <Box sx={{ width: '25%', textAlign: 'center' }}>Actions</Box>
            </Box>
            
            <List sx={{ p: 0 }}>
              {filteredItems.map((item) => (
                <React.Fragment key={item.id}>
                  <ListItem 
                    sx={{ 
                      py: 1.5,
                      flexDirection: { xs: 'column', md: 'row' }, // Stack vertically on mobile
                      alignItems: { xs: 'flex-start', md: 'center' },
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    {/* Name - Full width on mobile */}
                    <Box sx={{ 
                      display: 'flex', 
                      width: { xs: '100%', md: '40%' }, 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      mb: { xs: 1, md: 0 }
                    }}
                    onClick={() => handleItemClick(item)}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getFileIcon(item)}
                      </ListItemIcon>
                      <Typography noWrap>{item.name}</Typography>
                    </Box>
                    
                    {/* Mobile info row */}
                    <Box sx={{ 
                      display: { xs: 'flex', md: 'none' },
                      width: '100%',
                      justifyContent: 'space-between',
                      mb: 1,
                      pl: 5 // Align with name
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        {item.lastModifiedDateTime ? 
                          new Date(item.lastModifiedDateTime).toLocaleDateString() : '-'}
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary">
                        {item.folder ? 
                          `${item.folder.childCount} items` : 
                          formatFileSize(item.size)}
                      </Typography>
                    </Box>
                    
                    {/* Desktop info columns - hidden on mobile */}
                    <Box sx={{ width: '20%', display: { xs: 'none', md: 'block' } }}>
                      {item.lastModifiedDateTime ? 
                        new Date(item.lastModifiedDateTime).toLocaleDateString() : '-'}
                    </Box>
                    <Box sx={{ width: '15%', display: { xs: 'none', md: 'block' } }}>
                      {item.folder ? 
                        `${item.folder.childCount} items` : 
                        formatFileSize(item.size)}
                    </Box>
                    
                    {/* Actions - Adapt for mobile */}
                    <Box sx={{ 
                      width: { xs: '100%', md: '25%' }, 
                      display: 'flex', 
                      justifyContent: { xs: 'flex-start', md: 'center' },
                      pl: { xs: 5, md: 0 } // Align with name on mobile
                    }}>
                      {item.folder ? (
                        <Button 
                          variant="text" 
                          color="primary" 
                          size="small"
                          startIcon={<FolderIcon />} 
                          onClick={() => handleFolderClick(item)}
                        >
                          Open
                        </Button>
                      ) : (
                        <>
                          <Button 
                            variant="text" 
                            color="primary" 
                            size="small"
                            startIcon={<ViewIcon />} 
                            onClick={() => handleFileClick(item)}
                            sx={{ mr: 1 }}
                          >
                            {/* Text for desktop, icon only for mobile */}
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>View</Box>
                          </Button>
                          <Button 
                            variant="text" 
                            color="secondary" 
                            size="small"
                            startIcon={<DownloadIcon />} 
                            onClick={() => handleFileDownload(item)}
                          >
                            {/* Text for desktop, icon only for mobile */}
                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Download</Box>
                          </Button>
                        </>
                      )}
                    </Box>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              
              {filteredItems.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  {searchTerm ? 
                    `No items match your search "${searchTerm}"` : 
                    "This folder is empty"}
                </Box>
              )}
            </List>
          </Paper>
        ) : (
          // Normal Mode - Grid View (like SharePoint)
          <Box>
            <Grid container spacing={1}>
              {filteredItems.map((item) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={item.id}>
                  <Card 
                    elevation={0}
                    sx={{ 
                      cursor: 'pointer',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      transition: 'all 0.2s ease',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': { 
                        boxShadow: 2,
                        borderColor: '#0078d4' 
                      }
                    }}
                    onClick={() => handleItemClick(item)}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      p: { xs: 1, sm: 2 },
                      backgroundColor: '#f5f5f5',
                      height: { xs: 100, sm: 120, md: 140 }
                    }}>
                      {getFileIcon(item)}
                    </Box>
                    <CardContent sx={{ 
                      p: { xs: 1, sm: 1.5 }, 
                      pt: { xs: 1.5, sm: 2 }, 
                      flexGrow: 1,
                      '&:last-child': { pb: 1 }
                    }}>
                      <Typography 
                        variant="body2" 
                        noWrap 
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        component="div" 
                        sx={{ 
                          mt: 0.5, 
                          fontSize: { xs: '0.65rem', sm: '0.7rem' }
                        }}
                      >
                        {item.folder 
                          ? `${item.folder.childCount} item${item.folder.childCount !== 1 ? 's' : ''}` 
                          : formatFileSize(item.size)
                        }
                      </Typography>
                      {/* Hide modified date on smallest screens */}
                      {item.lastModifiedDateTime && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary" 
                          component="div"
                          sx={{ 
                            display: { xs: 'none', sm: 'block' },
                            fontSize: { xs: '0.65rem', sm: '0.7rem' }
                          }}
                        >
                          Modified: {new Date(item.lastModifiedDateTime).toLocaleDateString()}
                        </Typography>
                      )}
                      
                      {/* File actions for mobile - shown on hover/touch */}
                      {!item.folder && (
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          mt: { xs: 0.5, sm: 1 }
                        }}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFileClick(item);
                            }}
                            sx={{ 
                              padding: 0.5,
                              mx: -0.5,
                              visibility: { sm: 'hidden' },
                              '.MuiSvgIcon-root': { fontSize: '1rem' }
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFileDownload(item);
                            }}
                            sx={{ 
                              padding: 0.5,
                              mx: -0.5,
                              visibility: { sm: 'hidden' },
                              '.MuiSvgIcon-root': { fontSize: '1rem' }
                            }}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {filteredItems.length === 0 && (
              <Box sx={{ 
                p: 8, 
                textAlign: 'center', 
                border: '1px dashed #ccc',
                borderRadius: 1,
                backgroundColor: '#f9f9f9' 
              }}>
                <Typography variant="body1" color="text.secondary">
                  {searchTerm ? 
                    `No items match your search "${searchTerm}"` : 
                    "This folder is empty"}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
      
      {/* PDF Viewer Modal */}
      {pdfViewerOpen && (
        <PDFViewer
          open={pdfViewerOpen}
          onClose={handleClosePdfViewer}
          fileUrl={fileViewUrl}
        />
      )}
      
      {/* Office Document Viewer Modal */}
      <Modal
        open={officeViewerOpen}
        onClose={() => setOfficeViewerOpen(false)}
        aria-labelledby="office-viewer-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '95vw',
            height: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <IconButton
            onClick={() => setOfficeViewerOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              zIndex: 1000,
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          
          <iframe 
            src={fileViewUrl}
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
            title={selectedFile?.name || 'Office Document'}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default DocumentBrowser;